/**
 * Typed OpenAPI client factory.
 *
 * Surfaces the generated `createApiClient` from `@shoppex/contracts` with the
 * SDK's configured `apiBaseUrl`. This lets consumers of `@shoppexio/storefront`
 * call any public Dev API endpoint with full end-to-end types without
 * rebuilding a client from scratch.
 *
 * Prefer the high-level modules (`shoppex.getProducts()` etc.) for common
 * read flows — they handle caching, pagination defaults, and storefront
 * scoping. Drop down to `shoppex.client()` only when you need an endpoint
 * the high-level API does not cover yet.
 */

import { createApiClient } from '@shoppex/contracts';
import { DEFAULT_API_BASE_URL, getConfig } from './config';

type ApiClient = ReturnType<typeof createApiClient>;

let cachedClient: ApiClient | null = null;
let cachedBaseUrl: string | null = null;

/**
 * Return a typed OpenAPI client bound to the SDK's configured API base URL.
 * The client is cached per base URL and recreated when the SDK is re-initialized
 * against a different host.
 */
export function getTypedClient(token?: string): ApiClient {
  const config = getConfig();
  const baseUrl = config.apiBaseUrl ?? DEFAULT_API_BASE_URL;

  if (cachedClient && cachedBaseUrl === baseUrl && !token) {
    return cachedClient;
  }

  const client = createApiClient(baseUrl, token);

  if (!token) {
    cachedClient = client;
    cachedBaseUrl = baseUrl;
  }

  return client;
}

/**
 * Reset the cached client. Called when the SDK is re-initialized so that a new
 * `apiBaseUrl` takes effect immediately on the next `client()` call.
 */
export function resetTypedClient(): void {
  cachedClient = null;
  cachedBaseUrl = null;
}
